/**
 * SiteSurveyPanel — live field survey notes + photos.
 *
 * Attaches to either a Bid Desk record (linkType="bid_record") or a Project
 * Command Center project (linkType="project"). Captures photo + note per
 * observation, builds the branded report and emails it to stakeholders.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Plus, Loader2, Trash2, FileText, Send, ChevronLeft, MapPin,
  AlertTriangle, ClipboardList, Image as ImageIcon, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

const BE = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = {
  observation: "Observation", existing_condition: "Existing condition", issue: "Issue",
  access_constraint: "Access constraint", utility: "Utility", measurement: "Measurement",
  equipment: "Equipment", action_required: "Action required",
};
const SEV_CLASS = {
  info: "text-slate-600 bg-slate-100 border-slate-200",
  low: "text-teal-700 bg-teal-50 border-teal-200",
  medium: "text-amber-700 bg-amber-50 border-amber-200",
  high: "text-red-700 bg-red-50 border-red-200",
};

const BLANK_ENTRY = {
  caption: "", note: "", area: "", entry_type: "observation", severity: "info",
  measurement: "", action_required: "", responsible: "",
};

export default function SiteSurveyPanel({ linkType, linkId, token, label = "", scope = "link" }) {
  const isDesk = scope === "all";
  const headers = { Authorization: `Bearer ${token}` };
  const [surveys, setSurveys] = useState([]);
  const [open, setOpen] = useState(null); // open survey doc
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [draft, setDraft] = useState({ title: "", visit_date: "", facility: "", address: "", purpose: "", areas: "", attendees: "" });
  const [entry, setEntry] = useState(BLANK_ENTRY);
  const [photo, setPhoto] = useState(null);
  const [share, setShare] = useState({ to: "", cc: "", message: "", attach_pdf: true });
  const [attachFor, setAttachFor] = useState(null);   // survey id awaiting a bid
  const [bids, setBids] = useState([]);

  const openAttach = async (sid) => {
    setAttachFor(sid);
    try {
      const r = await axios.get(`${API}/bid-desk/solicitations`, { headers });
      setBids(r.data.solicitations || r.data.records || []);
    } catch { toast.error("Could not load the bid list"); }
  };

  const attachToBid = async (bidId) => {
    try {
      await axios.put(`${API}/site-surveys/${attachFor}/link`,
        { link_type: "bid_record", link_id: bidId }, { headers });
      toast.success("Survey attached to the bid");
      setAttachFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not attach that survey"); }
  };

  const load = useCallback(async () => {
    if (!isDesk && !linkId) return;
    setLoading(true);
    try {
      const params = isDesk ? {} : { link_type: linkType, link_id: linkId };
      const r = await axios.get(`${API}/site-surveys`, { headers, params });
      setSurveys(r.data.surveys || []);
    } catch { /* quiet */ } finally { setLoading(false); }
  }, [linkId, linkType, isDesk, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openSurvey = async (sid) => {
    try {
      const r = await axios.get(`${API}/site-surveys/${sid}`, { headers });
      setOpen(r.data.survey);
    } catch { toast.error("Could not open that survey"); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/site-surveys`, {
        link_type: isDesk ? "standalone" : linkType, link_id: isDesk ? "" : linkId,
        title: draft.title, visit_date: draft.visit_date, facility: draft.facility,
        address: draft.address, purpose: draft.purpose,
        areas: draft.areas.split(",").map((a) => a.trim()).filter(Boolean),
        attendees: draft.attendees.split(",").map((a) => a.trim()).filter(Boolean)
          .map((a) => { const [name, company] = a.split("/"); return { name: (name || "").trim(), company: (company || "").trim() }; }),
      }, { headers });
      toast.success("Survey opened — start adding photos and notes");
      setShowNew(false);
      setDraft({ title: "", visit_date: "", facility: "", address: "", purpose: "", areas: "", attendees: "" });
      setOpen(r.data.survey);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the survey"); } finally { setBusy(false); }
  };

  const addEntry = async () => {
    if (!entry.note.trim() && !entry.caption.trim() && !photo) {
      toast.error("Add a note or a photo first"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(entry).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/site-surveys/${open.id}/entries`, fd, { headers });
      toast.success("Observation saved");
      setEntry(BLANK_ENTRY); setPhoto(null);
      openSurvey(open.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the observation"); } finally { setBusy(false); }
  };

  const removeEntry = async (eid) => {
    try {
      await axios.delete(`${API}/site-surveys/${open.id}/entries/${eid}`, { headers });
      openSurvey(open.id); load();
    } catch { toast.error("Could not remove that observation"); }
  };

  const saveSummary = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/site-surveys/${open.id}`, {
        summary: open.summary || "", status: open.status || "in_progress",
      }, { headers });
      toast.success("Survey saved");
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };

  const removeSurvey = async (sid) => {
    if (!window.confirm("Delete this survey and every observation on it?")) return;
    try {
      await axios.delete(`${API}/site-surveys/${sid}`, { headers });
      setOpen(null); load();
      toast.success("Survey deleted");
    } catch { toast.error("Could not delete"); }
  };

  const sendShare = async () => {
    const to = share.to.split(",").map((s) => s.trim()).filter(Boolean);
    if (!to.length) { toast.error("Add at least one stakeholder email"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/site-surveys/${open.id}/share`, {
        to, cc: share.cc.split(",").map((s) => s.trim()).filter(Boolean),
        message: share.message, attach_pdf: share.attach_pdf,
      }, { headers });
      if (r.data.success) {
        toast.success(`Report sent to ${to.length} stakeholder(s)`);
        setShowShare(false);
      } else {
        toast.error(r.data.share?.error || "The mail provider rejected the message");
      }
      openSurvey(open.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the report"); } finally { setBusy(false); }
  };

  // --------------------------------------------------------------- open view
  if (open) {
    const entries = open.entries || [];
    return (
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="site-survey-open">
        <div className="flex items-start justify-between gap-3 flex-wrap border-b border-slate-100 p-4">
          <div className="flex items-start gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setOpen(null); load(); }} data-testid="survey-back-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-sky-700">
                Live site survey · {open.id}
              </p>
              <h3 className="text-base font-black text-slate-900">{open.title}</h3>
              <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {open.facility || open.linked_name || "—"} · {open.visit_date}
                {open.project_id ? ` · carried to ${open.project_id}` : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="survey-report-btn"
              onClick={() => openAuthedDoc(`/site-surveys/${open.id}/report`, { token, fmt: "pdf" })}>
              <FileText className="w-3.5 h-3.5 mr-1" /> Report PDF
            </Button>
            <Button size="sm" className="bg-sky-700 hover:bg-sky-800" data-testid="survey-share-btn"
              onClick={() => {
                setShare((s) => ({
                  ...s,
                  to: (open.stakeholders || []).map((x) => x.email).filter(Boolean).join(", "),
                }));
                setShowShare(true);
              }}>
              <Send className="w-3.5 h-3.5 mr-1" /> Email stakeholders
            </Button>
            <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
              onClick={() => removeSurvey(open.id)} data-testid="survey-delete-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Survey summary</label>
            <Textarea rows={2} value={open.summary || ""} data-testid="survey-summary-input"
              placeholder="What the walk established — conditions, decisions, what the owner still owes us…"
              onChange={(e) => setOpen((s) => ({ ...s, summary: e.target.value }))} />
            <div className="flex justify-end mt-2">
              <Button size="sm" variant="outline" onClick={saveSummary} disabled={busy} data-testid="survey-save-summary">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Save summary
              </Button>
            </div>
          </div>

          {/* add observation */}
          <div className="rounded-lg border-2 border-dashed border-sky-200 bg-sky-50/40 p-3 space-y-2"
            data-testid="survey-entry-form">
            <p className="text-[10px] font-black uppercase tracking-wider text-sky-800">
              Add an observation
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input className="bg-white" placeholder="Caption — e.g. Existing RO skid, Room 2.114"
                value={entry.caption} onChange={(e) => setEntry((x) => ({ ...x, caption: e.target.value }))}
                data-testid="survey-entry-caption" />
              <Input className="bg-white" placeholder="Area / room"
                value={entry.area} onChange={(e) => setEntry((x) => ({ ...x, area: e.target.value }))}
                data-testid="survey-entry-area" />
            </div>
            <Textarea rows={2} className="bg-white" placeholder="Field note — what you saw, what it means for the install…"
              value={entry.note} onChange={(e) => setEntry((x) => ({ ...x, note: e.target.value }))}
              data-testid="survey-entry-note" />
            <div className="grid sm:grid-cols-4 gap-2">
              <select value={entry.entry_type} className="h-9 text-xs border border-slate-300 rounded-md px-2 bg-white"
                onChange={(e) => setEntry((x) => ({ ...x, entry_type: e.target.value }))} data-testid="survey-entry-type">
                {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select value={entry.severity} className="h-9 text-xs border border-slate-300 rounded-md px-2 bg-white"
                onChange={(e) => setEntry((x) => ({ ...x, severity: e.target.value }))} data-testid="survey-entry-severity">
                {["info", "low", "medium", "high"].map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
              <Input className="bg-white text-xs" placeholder="Measured — 14 ft ceiling"
                value={entry.measurement} onChange={(e) => setEntry((x) => ({ ...x, measurement: e.target.value }))}
                data-testid="survey-entry-measurement" />
              <Input className="bg-white text-xs" placeholder="Responsible party"
                value={entry.responsible} onChange={(e) => setEntry((x) => ({ ...x, responsible: e.target.value }))}
                data-testid="survey-entry-responsible" />
            </div>
            <Input className="bg-white text-xs" placeholder="Action required (optional) — who has to do what before we mobilise"
              value={entry.action_required}
              onChange={(e) => setEntry((x) => ({ ...x, action_required: e.target.value }))}
              data-testid="survey-entry-action" />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-white border border-slate-300 text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-50">
                <Camera className="w-4 h-4 text-sky-700" />
                {photo ? photo.name.slice(0, 28) : "Take / attach photo"}
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="survey-entry-photo" />
              </label>
              <div className="flex items-center gap-2">
                {photo && (
                  <Button size="sm" variant="ghost" onClick={() => setPhoto(null)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={addEntry} disabled={busy}
                  data-testid="survey-entry-save">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                  Save observation
                </Button>
              </div>
            </div>
          </div>

          {/* entries */}
          {entries.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="survey-entries-empty">
              No observations yet — the first photo you take shows up here.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="survey-entries">
              {entries.map((e, i) => (
                <div key={e.id} className="rounded-lg border border-slate-200 overflow-hidden bg-white"
                  data-testid={`survey-entry-${i}`}>
                  {e.photo_url ? (
                    <a href={`${BE}${e.photo_url}`} target="_blank" rel="noreferrer">
                      <img src={`${BE}${e.photo_url}`} alt={e.caption || "Site photo"}
                        className="w-full h-40 object-cover" />
                    </a>
                  ) : (
                    <div className="w-full h-40 bg-slate-50 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                  <div className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[9px] font-black uppercase tracking-wider border rounded px-1.5 py-0.5 ${SEV_CLASS[e.severity] || SEV_CLASS.info}`}>
                        {TYPE_LABEL[e.entry_type] || "Observation"}
                      </span>
                      <Button size="sm" variant="ghost" className="h-6 px-1 text-red-500 hover:bg-red-50"
                        onClick={() => removeEntry(e.id)} data-testid={`survey-entry-delete-${i}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    <p className="text-sm font-bold text-slate-900 mt-1.5">{e.caption || "Field observation"}</p>
                    {e.note && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{e.note}</p>}
                    <p className="text-[11px] text-slate-400 mt-1.5">
                      {[e.area, e.measurement].filter(Boolean).join(" · ") || "—"}
                    </p>
                    {e.action_required && (
                      <p className="text-[11px] text-amber-800 bg-amber-50 border-l-2 border-amber-400 px-2 py-1 mt-1.5">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        {e.action_required}{e.responsible ? ` — ${e.responsible}` : ""}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!!(open.shares || []).length && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="survey-share-log">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Shared with</p>
              {(open.shares || []).map((s) => (
                <p key={s.id} className="text-[11px] text-slate-600">
                  {new Date(s.at).toLocaleString()} → {s.to.join(", ")}
                  {s.sent ? <span className="text-emerald-700 font-semibold"> · sent</span>
                    : <span className="text-red-600 font-semibold"> · failed: {s.error}</span>}
                </p>
              ))}
            </div>
          )}
        </div>

        <Dialog open={showShare} onOpenChange={setShowShare}>
          <DialogContent className="max-w-lg" data-testid="survey-share-dialog">
            <DialogHeader><DialogTitle>Email the survey report</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Input placeholder="To — comma separated" value={share.to}
                onChange={(e) => setShare((s) => ({ ...s, to: e.target.value }))} data-testid="survey-share-to" />
              <Input placeholder="CC (optional)" value={share.cc}
                onChange={(e) => setShare((s) => ({ ...s, cc: e.target.value }))} data-testid="survey-share-cc" />
              <Textarea rows={3} placeholder="Note at the top of the email (optional)" value={share.message}
                onChange={(e) => setShare((s) => ({ ...s, message: e.target.value }))} data-testid="survey-share-message" />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={share.attach_pdf} data-testid="survey-share-attach"
                  onChange={(e) => setShare((s) => ({ ...s, attach_pdf: e.target.checked }))} />
                Attach the branded PDF as well as putting the report in the email body
              </label>
              <div className="flex justify-end">
                <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={sendShare} disabled={busy}
                  data-testid="survey-share-send">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  Send report
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // --------------------------------------------------------------- list view
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="site-survey-panel">
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-sky-700">Field intake</p>
          <h3 className="text-base font-black text-slate-900 inline-flex items-center gap-2">
            <Camera className="w-4 h-4 text-sky-700" /> Live site survey
          </h3>
          <p className="text-xs text-slate-500">
            {isDesk
              ? "Every site survey across the desk. Start one before a bid exists and attach it to the bid later."
              : `Walk the site, capture photos and notes against ${label || "this record"} and email the branded report to stakeholders.`}
          </p>
        </div>
        <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={() => setShowNew(true)}
          data-testid="survey-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New survey
        </Button>
      </div>

      <div className="p-4">
        {loading ? (
          <p className="text-sm text-slate-400 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </p>
        ) : surveys.length === 0 ? (
          <p className="text-sm text-slate-400 py-4" data-testid="survey-list-empty">
            No site surveys yet. Open one before the walk and capture photos from your phone as you go.
          </p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="survey-list">
            {surveys.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 py-2.5 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900">{s.title}</p>
                  <p className="text-[11px] text-slate-500">
                    {s.visit_date} · {s.entry_count} observation{s.entry_count === 1 ? "" : "s"} ·{" "}
                    {s.photo_count} photo{s.photo_count === 1 ? "" : "s"}
                    {s.issue_count ? <span className="text-red-600 font-semibold"> · {s.issue_count} flagged</span> : null}
                    {s.link_type === "bid_record" && s.project_id ? ` · carried to ${s.project_id}` : ""}
                  </p>
                  {isDesk && (
                    <p className="text-[10px] font-bold uppercase tracking-wider mt-0.5
                      text-slate-500" data-testid={`survey-link-${s.id}`}>
                      {s.link_type === "standalone"
                        ? "Not attached to a bid yet"
                        : `${s.link_type === "project" ? "Project" : "Bid"}: ${s.linked_name || s.link_id}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openSurvey(s.id)}
                    data-testid={`survey-open-${s.id}`}>
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> Open
                  </Button>
                  {isDesk && s.link_type === "standalone" && (
                    <Button size="sm" variant="outline" onClick={() => openAttach(s.id)}
                      data-testid={`survey-attach-${s.id}`}>
                      <MapPin className="w-3.5 h-3.5 mr-1" /> Attach to bid
                    </Button>
                  )}
                  <Button size="sm" variant="ghost"
                    onClick={() => openAuthedDoc(`/site-surveys/${s.id}/report`, { token, fmt: "pdf" })}
                    data-testid={`survey-pdf-${s.id}`}>
                    <FileText className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!attachFor} onOpenChange={(v) => !v && setAttachFor(null)}>
        <DialogContent className="max-w-lg" data-testid="survey-attach-dialog">
          <DialogHeader><DialogTitle>Attach this survey to a bid</DialogTitle></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100">
            {bids.length === 0 ? (
              <p className="text-sm text-slate-400 py-4">No bid records on the desk yet.</p>
            ) : bids.map((b) => (
              <button key={b.id} onClick={() => attachToBid(b.id)}
                className="w-full text-left py-2.5 hover:bg-slate-50 px-2 rounded"
                data-testid={`survey-attach-pick-${b.id}`}>
                <p className="text-sm font-semibold text-slate-900">
                  {b.title || b.parsed?.title || b.id}
                </p>
                <p className="text-[11px] text-slate-500">{b.agency || b.parsed?.agency || ""} · {b.status}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg" data-testid="survey-new-dialog">
          <DialogHeader><DialogTitle>Open a site survey</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Title — e.g. Pre-bid walk, Dialysis water room"
              value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              data-testid="survey-new-title" />
            <div className="grid sm:grid-cols-2 gap-2">
              <Input type="date" value={draft.visit_date}
                onChange={(e) => setDraft((d) => ({ ...d, visit_date: e.target.value }))}
                data-testid="survey-new-date" />
              <Input placeholder="Facility" value={draft.facility}
                onChange={(e) => setDraft((d) => ({ ...d, facility: e.target.value }))}
                data-testid="survey-new-facility" />
            </div>
            <Input placeholder="Site address" value={draft.address}
              onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
              data-testid="survey-new-address" />
            <Input placeholder="Purpose of the visit" value={draft.purpose}
              onChange={(e) => setDraft((d) => ({ ...d, purpose: e.target.value }))}
              data-testid="survey-new-purpose" />
            <Input placeholder="Areas walked — comma separated" value={draft.areas}
              onChange={(e) => setDraft((d) => ({ ...d, areas: e.target.value }))}
              data-testid="survey-new-areas" />
            <Input placeholder="Attendees — Name/Company, Name/Company" value={draft.attendees}
              onChange={(e) => setDraft((d) => ({ ...d, attendees: e.target.value }))}
              data-testid="survey-new-attendees" />
            <div className="flex justify-end">
              <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={create} disabled={busy}
                data-testid="survey-new-save">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                Open survey
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
